import React, { useState, useCallback } from 'react';
import {
  Slider,
  Box,
  Typography,
  Input,
  DatePicker,
  Dropdown,
  FileUpload,
  Table as TableComponent,
  Button,
} from '@lucid/lego';
import type {
  OrderDelivery,
  DeliveryLineItem,
} from '../../../../api/services/lupp/order/importer/dto.importer';
import { useConfirmDeliveryPickup } from '../../../../api/services/lupp/order/importer/importer';
import { useFileUpload } from '../../../../hooks/useFileUpload';
import styles from './ConfirmPickupSlider.module.scss';
import { useUserIdentity } from '@lucid/portal-core';
import { DOCUMENT_CATEGORY, FILEHUB_MODULE_NAME } from '../../../../constants/constants';
import { formatVehicleModelName } from '../../../../utils/tools';

// Keep this in sync with the supported formats listed in the helper text below.
const SUPPORTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.doc,.docx,.png';

const PICKUP_TIME_OPTIONS = (() => {
  const options: { value: string; label: string }[] = [];
  for (let totalMins = 9 * 60; totalMins <= 21 * 60; totalMins += 30) {
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h > 12 ? h - 12 : h;
    options.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` });
  }
  return options;
})();

type ConfirmPickupSliderProps = {
  delivery: OrderDelivery;
  orderId: string;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  onClose: () => void;
  onSuccess?: () => void;
};

const ConfirmPickupSlider: React.FC<ConfirmPickupSliderProps> = ({
  delivery,
  orderId,
  theme,
  labels,
  onClose,
  onSuccess,
}) => {
  const sliderTheme = theme === 'light' ? 'dark' : 'light';
  const { userIdentity } = useUserIdentity();
  const firstName = userIdentity?.firstName ?? '';
  const lastName = userIdentity?.lastName ?? '';
  const userName = [firstName, lastName].filter(Boolean).join(' ');
  const userId = userIdentity?.auth0UserId ?? '';
  const email = userIdentity?.emailAddress ?? '';

  const [pickedUpDate, setPickedUpDate] = useState<Date | null>(null);
  const [pickedUpTime, setPickedUpTime] = useState('');
  const [pickedUpLocation, setPickedUpLocation] = useState('');
  const [displayName, setDisplayName] = useState(userName);
  const [fileIds, setFileIds] = useState<string[]>([]);

  const { mutate: confirmPickup, isPending } = useConfirmDeliveryPickup();

  const { getUploadUrl, handleFileRemove } = useFileUpload({
    customerId: delivery.importerId,
    category: DOCUMENT_CATEGORY.GENERIC,
    entityType: FILEHUB_MODULE_NAME.DELIVERY_MANAGEMENT,
    entityId: delivery.deliveryId,
    onUploadUrlSuccess: (response) => {
      setFileIds((prev) => [...prev, response.fileId]);
    },
  });

  const handleFileRemoveWithTracking = useCallback(
    (fileId?: string) => {
      if (fileId) setFileIds((prev) => prev.filter((id) => id !== fileId));
      handleFileRemove(fileId);
    },
    [handleFileRemove]
  );

  const pickedUpAt =
    pickedUpDate && pickedUpTime
      ? (() => {
          const y = pickedUpDate.getFullYear();
          const mo = String(pickedUpDate.getMonth() + 1).padStart(2, '0');
          const d = String(pickedUpDate.getDate()).padStart(2, '0');
          return `${y}-${mo}-${d}T${pickedUpTime}:00Z`;
        })()
      : '';

  const isSubmitDisabled =
    !pickedUpAt || !pickedUpLocation.trim() || !displayName.trim() || isPending;

  const handleSubmit = useCallback(() => {
    const confirmedLineItemIds = delivery.lineItems.map((li) => li.vehicleOrderId);
    const confirmedLineItemNumbers = delivery.lineItems
      .map((li) => li.vehicleOrderNumber)
      .filter((lineItemNumber): lineItemNumber is string => Boolean(lineItemNumber));

    const pickedUpForOrganization = {
      businessOrganizationId: userIdentity?.businessOrganizationId,
      auth0OrganizationId: userIdentity?.auth0OrganizationId,
      organizationName: userIdentity?.organizationName,
      businessPartnerOrgLocation: userIdentity?.businessPartnerOrgLocation,
      countryCode: userIdentity?.countryCode,
    };

    confirmPickup(
      {
        orderId,
        deliveryId: delivery.deliveryId,
        pickedUpAt,
        pickedUpLocation,
        pickedUpBy: {
          displayName,
          userId,
          email,
          userPortalId: userIdentity?.userPortalId,
          firstName: userIdentity?.firstName,
          lastName: userIdentity?.lastName,
        },
        pickedUpForOrganization,
        confirmedImporterOrderId: delivery.importerOrderId,
        confirmedImporterOrderNumber: delivery.importerOrderNumber,
        confirmedLineItemIds,
        confirmedLineItemNumbers,
        ...(fileIds.length > 0 ? { fileIds } : {}),
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
  }, [
    confirmPickup,
    orderId,
    delivery.deliveryId,
    delivery.importerOrderId,
    delivery.importerOrderNumber,
    delivery.lineItems,
    pickedUpAt,
    pickedUpLocation,
    displayName,
    userId,
    email,
    userIdentity,
    fileIds,
    onSuccess,
    onClose,
  ]);

  const columns = [
    {
      key: 'vehicleOrderNumber',
      title: labels?.['confirmPickup.table.orderNumber'] ?? 'Order Line #',
      width: '160px',
      sortable: false,
      render: (_: unknown, record: DeliveryLineItem) => (
        <Typography variant="body" size="small" theme={sliderTheme} color="white">
          {record.vehicleOrderNumber ?? '-'}
        </Typography>
      ),
    },
    {
      key: 'model',
      title: labels?.['confirmPickup.table.model'] ?? 'Model',
      width: '150px',
      sortable: false,
      render: (_: unknown, record: DeliveryLineItem) => (
        <Typography variant="body" size="small" theme={sliderTheme} color="white">
          {formatVehicleModelName(record.model)}
        </Typography>
      ),
    },
    {
      key: 'vin',
      title: labels?.['confirmPickup.table.vin'] ?? 'VIN',
      width: '180px',
      sortable: false,
      render: (_: unknown, record: DeliveryLineItem) => (
        <Typography variant="body" size="small" theme={sliderTheme} color="white">
          {record.vin ?? '-'}
        </Typography>
      ),
    },
  ];

  const footer = (
    <Box className={styles.footer}>
      <Button
        type="tertiary"
        color="neutral"
        theme={sliderTheme}
        label={labels?.['confirmPickup.cancel'] ?? 'Cancel'}
        dataTestId="confirm-pickup-cancel"
        gaComponent="confirm-pickup-slider"
        handleClick={onClose}
        disabled={isPending}
      />
      <Button
        type="primary"
        color="neutral"
        theme={sliderTheme}
        label={labels?.['confirmPickup.submit'] ?? 'Confirm Pickup'}
        dataTestId="confirm-pickup-submit"
        gaComponent="confirm-pickup-slider"
        handleClick={handleSubmit}
        disabled={isSubmitDisabled}
      />
    </Box>
  );

  return (
    <Slider
      isOpen
      onClose={onClose}
      title={`${labels?.['confirmPickup.title'] ?? 'Confirm Pickup'} – ${delivery.deliveryNumber}`}
      width="800px"
      direction="right"
      theme={sliderTheme}
      footer={footer}
    >
      <Box mb={24}>
        <Typography variant="subtitle" size="small" theme={sliderTheme} color="white">
          {labels?.['confirmPickup.iolisTitle'] ?? 'Importer Order Line Items'}
        </Typography>
        <Box pt={4}>
          <Typography variant="body" size="small" theme={sliderTheme} color="gray-3">
            {labels?.['confirmPickup.iolisSubtitle'] ?? 'All items will be confirmed for pickup.'}
          </Typography>
        </Box>
        <Box pt={12}>
          <TableComponent
            compact
            columns={columns}
            data={delivery.lineItems}
            theme={sliderTheme}
            selection={{ enabled: false }}
            pagination={{
              enabled: false,
            }}
            emptyState={{
              message: labels?.['confirmPickup.noLineItems'] ?? 'No line items.',
            }}
            labels={{
              showingItems:
                labels?.['table.showingItems'] ?? 'Showing {{start}}-{{end}} of {{total}} Items',
              pageOf: labels?.['table.pageOf'] ?? '{{current}} of {{total}}',
            }}
            gaComponent="confirm-pickup-ioli-table"
            dataTestId="confirm-pickup-ioli-table"
          />
        </Box>
      </Box>

      <Box mb={16} className={styles.dateTimeRow}>
        <Box className={styles.dateTimeCol}>
          <DatePicker
            name="pickedUpDate"
            label={labels?.['confirmPickup.pickedUpDate'] ?? 'Pickup Date'}
            theme={sliderTheme}
            required
            value={pickedUpDate}
            onChange={(date: Date) => setPickedUpDate(date)}
          />
        </Box>
        <Box className={styles.dateTimeCol}>
          <Dropdown
            name="pickedUpTime"
            label={labels?.['confirmPickup.pickedUpTime'] ?? 'Pickup Time'}
            theme={sliderTheme}
            required
            options={PICKUP_TIME_OPTIONS}
            selectedOption={pickedUpTime}
            handleSelect={(val) => setPickedUpTime(val)}
            placeholder={labels?.['confirmPickup.timePlaceholder'] ?? 'Select Time'}
          />
        </Box>
      </Box>

      <Box mb={16}>
        <Input
          name="pickedUpLocation"
          label={labels?.['confirmPickup.pickedUpLocation'] ?? 'Pickup Location'}
          theme={sliderTheme}
          required
          controlled
          value={pickedUpLocation}
          onChange={(e) => setPickedUpLocation(e.target.value)}
          dataTestId="confirm-pickup-location"
        />
      </Box>

      <Box mb={16}>
        <Input
          name="pickedUpBy"
          label={labels?.['confirmPickup.pickedUpBy'] ?? 'Picked Up By'}
          theme={sliderTheme}
          required
          controlled
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          dataTestId="confirm-pickup-by"
        />
      </Box>

      <Box mb={8}>
        <FileUpload
          dataTestId="confirm-pickup-file-upload"
          theme={sliderTheme}
          variant="dropzone"
          multiple
          removable
          label={labels?.['confirmPickup.documents'] ?? 'Supporting Documents'}
          helperText={
            labels?.['confirmPickup.documentsHelper'] ??
            'PDF, JPG, JPEG, DOC, DOCX, PNG files supported (max 10 MB each)'
          }
          accept={SUPPORTED_FILE_TYPES}
          getUploadUrl={getUploadUrl}
          onFileRemove={handleFileRemoveWithTracking}
        />
      </Box>

      <Box pt={16} pb={16} pl={16} pr={16} mt={8} className={styles.legalNotice}>
        <Typography variant="body" size="small" theme={sliderTheme} color="gray-3">
          {labels?.['confirmPickup.legalNotice'] ??
            'By submitting this confirmation, you certify that all pickup details provided are accurate and complete. This action is irreversible — Lucid will use this information to generate an Invoice to your organization. Please ensure all relevant proof-of-pickup documents are uploaded above, and verify all details carefully before proceeding.'}
        </Typography>
      </Box>
    </Slider>
  );
};

export default ConfirmPickupSlider;
